/**
 * Global auth middleware that manages access based on authentication status.
 * - authRoutes: Only for unauthenticated users (/, /authorize, /sign_in)
 * - publicRoutes: Accessible to everyone (/styleguide, /user-satisfaction)
 * - All other routes: Only for authenticated users
 */
export default defineNuxtRouteMiddleware(async (to) => {
  // Only run on client-side
  if (!import.meta.client) {
    return
  }

  const userStore = useUserStore()
  const localePath = useLocalePath()

  // Routes only for unauthenticated users (auth pages)
  const authRoutes = ['/', '/authorize', '/sign-in']

  // Routes accessible to everyone (public pages)
  const publicRoutes = [
    '/styleguide',
    '/logout',
    '/cookies',
    '/accessibility',
    '/privacy',
    '/auth-error',
    '/not-eligible',
  ]

  // Wait for user initialization if not yet done
  if (!userStore.isInitialized) {
    await userStore.fetchUser()
  }

  // Strip locale prefix for route matching (e.g. /en/subsidies → /subsidies)
  const cleanPath = to.path.replace(/^\/en(\/|$)/, '/')

  const isAuthenticated = !!userStore.user
  const isAuthRoute = authRoutes.includes(cleanPath)
  const isPublicRoute = publicRoutes.includes(cleanPath)

  // Public routes are accessible to everyone
  if (isPublicRoute) {
    return
  }

  // Signed in via CY Login but not linked to a CPS employer profile (not
  // eligible). Send them to the eligibility page. /not-eligible is public, so
  // this can't loop. fetchUser keeps the user logged out (user === null) for
  // this case, hence the dedicated flag.
  if (userStore.hasCpswebError && cleanPath !== '/not-eligible') {
    return navigateTo(localePath('/not-eligible'))
  }

  // If authenticated user tries to access auth routes, redirect to /subsidies
  if (isAuthenticated && isAuthRoute) {
    return navigateTo(localePath('/subsidies'))
  }

  // If unauthenticated user tries to access protected route, redirect to home
  if (!isAuthenticated && !isAuthRoute) {
    return navigateTo(localePath('/'))
  }
})
